import "./styles/global.css";
import "./styles/pages.css";

import { initSmoothScroll, getLenis } from "./lib/scroll";
import { initCursor } from "./lib/cursor";
import { runPreloader } from "./lib/preloader";
import { renderHome } from "./pages/home";
import { renderStory } from "./pages/story";
import { renderDay } from "./pages/day";
import { renderAccommodation } from "./pages/accommodation";
import { renderGallery } from "./pages/gallery";
import { renderRsvp } from "./pages/rsvp";
import { renderGifts } from "./pages/gifts";
import { renderFaq } from "./pages/faq";
import { renderAdmin } from "./pages/admin";
import { buzz } from "./lib/haptics";
import { setupEasterEgg } from "./lib/easterEgg";

// Disable browser's automatic scroll restoration so reopens always start at
// the top, regardless of where the user was when they last closed the tab.
if ("scrollRestoration" in history) {
  history.scrollRestoration = "manual";
}
// Strip any leftover hash before anything renders so the browser can't
// auto-scroll to it. Deep-link sharing is intentionally not supported —
// every visit lands on the home.
if (window.location.hash) {
  history.replaceState(null, "", window.location.pathname);
}

async function main() {
  const cursor = initCursor();

  await runPreloader({
    textureUrls: [],
    loadTexture: async () => null,
  });

  initSmoothScroll();

  const root = document.getElementById("app") as HTMLElement;

  if (window.location.pathname === "/admin") {
    document.querySelector(".site-header")?.classList.add("site-header--hidden");
    renderAdmin(root);
    cursor?.refresh();
    return;
  }

  renderHome(root);
  renderDay(root);
  renderAccommodation(root);
  renderFaq(root);
  renderGifts(root);
  renderStory(root);
  renderRsvp(root);
  renderGallery(root);

  setupMobileMenu();

  document.addEventListener("click", (e) => {
    const target = (e.target as HTMLElement)?.closest("a[data-link]") as
      | HTMLAnchorElement
      | null;
    if (!target) return;
    const href = target.getAttribute("href");
    if (!href || !href.startsWith("#")) return;
    e.preventDefault();

    // If a menu link was clicked, close the menu first so the scroll lands
    // on the right offset (and the body unlocks).
    if (target.matches("[data-menu-link]")) closeMobileMenu();

    const id = href.slice(1);
    const section = document.getElementById(id);
    if (!section) return;
    const lenis = getLenis();
    if (lenis) {
      lenis.scrollTo(section, { offset: -16 });
    } else {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    // Intentionally NOT updating window.location — keeps the URL clean so
    // reopening a closed tab lands on the home, not the last-clicked section.
    // Deep-link sharing (e.g. /#rsvp) still works via the initial-load handler.
  });

  setupActiveLinkTracking();
  setupHeaderTint();
  setupStickyCta();
  setupEasterEgg();
  cursor?.refresh();
}

/**
 * Sticky bottom RSVP CTA on mobile: slides up after the user scrolls past
 * the hero, hides when the RSVP section is in view (already there) or when
 * the virtual keyboard is open (typing in the form).
 */
function setupStickyCta() {
  const cta = document.querySelector<HTMLElement>(".sticky-cta");
  const rsvp = document.getElementById("rsvp");
  if (!cta || !rsvp) return;

  let isVisible = false;
  let rsvpInView = false;
  let keyboardOpen = false;

  const setVisible = (v: boolean) => {
    if (v === isVisible) return;
    isVisible = v;
    cta.classList.toggle("is-visible", v);
    cta.setAttribute("aria-hidden", v ? "false" : "true");
  };

  const update = () => {
    const pastHero = window.scrollY > window.innerHeight * 0.7;
    setVisible(pastHero && !rsvpInView && !keyboardOpen);
  };

  // Hide while the RSVP section is in view
  const io = new IntersectionObserver(
    ([entry]) => {
      rsvpInView = !!entry?.isIntersecting;
      update();
    },
    { rootMargin: "0px 0px -30% 0px", threshold: 0 },
  );
  io.observe(rsvp);

  window.addEventListener("scroll", update, { passive: true });
  window.addEventListener("resize", update, { passive: true });

  // Detect virtual keyboard via the visualViewport API (iOS + modern Android)
  if ("visualViewport" in window && window.visualViewport) {
    const baseHeight = window.innerHeight;
    window.visualViewport.addEventListener("resize", () => {
      const vh = window.visualViewport!.height;
      // Keyboard considered "open" if visual viewport shrinks by >150px
      keyboardOpen = baseHeight - vh > 150;
      update();
    });
  }

  update();
}

/**
 * As the user scrolls, swap the header's tint to match the section currently
 * sitting under it — a soft palette tour through the page.
 */
function setupHeaderTint() {
  const header = document.querySelector<HTMLElement>(".site-header");
  if (!header) return;

  // Map each section id to a palette tint defined in global.css.
  // Order here mirrors the visual scroll order for clarity.
  const tints: Record<string, string> = {
    "tuis":          "cream",
    "dag":           "bg-deep",
    "akkommodasie":  "sage-soft",
    "faq":           "blush",
    "geskenke":      "coral",
    "ons-verhaal":   "sage",
    "rsvp":          "terracotta",
    "galery":        "cream",
  };

  const sections = Object.keys(tints)
    .map((id) => document.getElementById(id))
    .filter((s): s is HTMLElement => s !== null);

  if (sections.length === 0) return;

  // Probe the document at "just below the header" — whichever section
  // overlaps that line is the one we tint to.
  let lastTint = "";
  const probeOffset = 80; // px below the very top of the viewport

  const update = () => {
    const probeY = window.scrollY + probeOffset;
    for (const section of sections) {
      const top = section.offsetTop;
      const bottom = top + section.offsetHeight;
      if (probeY >= top && probeY < bottom) {
        const tint = tints[section.id] ?? "cream";
        if (tint !== lastTint) {
          header.setAttribute("data-tint", tint);
          lastTint = tint;
        }
        return;
      }
    }
    // Past the last section — keep the last tint (or fall back to cream)
  };

  // Set initial tint
  header.setAttribute("data-tint", "cream");

  window.addEventListener("scroll", update, { passive: true });
  window.addEventListener("resize", update, { passive: true });
  update();
}

function setupActiveLinkTracking() {
  // Track active section for both desktop nav AND mobile overlay menu
  const links = Array.from(
    document.querySelectorAll<HTMLAnchorElement>(
      ".site-header__nav a[data-link], .site-menu a[data-link]",
    ),
  );

  // Group by href so each section's IntersectionObserver toggles all matching links
  const sectionToLinks = new Map<HTMLElement, HTMLAnchorElement[]>();
  for (const link of links) {
    const href = link.getAttribute("href") ?? "";
    if (!href.startsWith("#")) continue;
    const section = document.getElementById(href.slice(1));
    if (!section) continue;
    const arr = sectionToLinks.get(section) ?? [];
    arr.push(link);
    sectionToLinks.set(section, arr);
  }

  if (sectionToLinks.size === 0) return;

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const activeSection = entry.target as HTMLElement;
        for (const [section, sectionLinks] of sectionToLinks) {
          const isActive = section === activeSection;
          for (const link of sectionLinks) link.classList.toggle("is-active", isActive);
        }
      }
    },
    { rootMargin: "-40% 0px -55% 0px", threshold: 0 },
  );

  for (const section of sectionToLinks.keys()) observer.observe(section);
}

/**
 * Wire the hamburger button: opens/closes the full-screen overlay menu, locks
 * background scroll while open, closes on Esc, and auto-closes on resize >768.
 */
function setupMobileMenu() {
  const burger = document.querySelector<HTMLButtonElement>(".site-header__burger");
  const menu = document.querySelector<HTMLElement>(".site-menu");
  if (!burger || !menu) return;

  burger.addEventListener("click", () => {
    const open = burger.getAttribute("aria-expanded") === "true";
    if (open) closeMobileMenu();
    else openMobileMenu();
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && menu.getAttribute("aria-hidden") === "false") {
      closeMobileMenu();
    }
  });

  let resizeTimer: number | undefined;
  window.addEventListener("resize", () => {
    if (resizeTimer) window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      if (window.innerWidth > 768 && menu.getAttribute("aria-hidden") === "false") {
        closeMobileMenu();
      }
    }, 100);
  });
}

function openMobileMenu() {
  const burger = document.querySelector<HTMLButtonElement>(".site-header__burger");
  const menu = document.querySelector<HTMLElement>(".site-menu");
  if (!burger || !menu) return;
  burger.setAttribute("aria-expanded", "true");
  menu.setAttribute("aria-hidden", "false");
  document.body.classList.add("menu-open");
  buzz("open");
}

function closeMobileMenu() {
  const burger = document.querySelector<HTMLButtonElement>(".site-header__burger");
  const menu = document.querySelector<HTMLElement>(".site-menu");
  if (!burger || !menu) return;
  burger.setAttribute("aria-expanded", "false");
  menu.setAttribute("aria-hidden", "true");
  document.body.classList.remove("menu-open");
}

main().catch((err) => {
  console.error("Failed to bootstrap:", err);
});
